import type { JSX, ReactNode } from 'react';

/**
 * Abstract, informative banner for a product card. Each illustration tells the
 * product's story in shapes: EduCore is the lifecycle from admission to the
 * graduation cap, InstitutionFlow is the maker-checker-authorizer pipeline,
 * Market is the bag between families and suppliers, Assist is the conversation,
 * and CampusOne is the hub every product orbits. Unknown codes (the portal
 * family, MFI, merchants) get the generic identity artwork: one sign-in to
 * many services.
 */

export function ProductArtwork({ code }: { code: string }) {
  const Artwork = artworkByCode[code] ?? PortalArtwork;
  return <Artwork />;
}

function Svg({ children }: { children: ReactNode }) {
  return (
    <svg viewBox="0 0 400 240" preserveAspectRatio="xMidYMid slice" role="img" aria-hidden="true">
      {children}
    </svg>
  );
}

function CampusOneArtwork() {
  return (
    <Svg>
      <defs>
        <linearGradient id="pa-core-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#EFF8FF" />
          <stop offset="1" stopColor="#D1EFFF" />
        </linearGradient>
        <linearGradient id="pa-core-hub" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0070F2" />
          <stop offset="1" stopColor="#0040A3" />
        </linearGradient>
      </defs>
      <rect width="400" height="240" fill="url(#pa-core-bg)" />
      <circle cx="330" cy="40" r="90" fill="#D1EFFF" opacity="0.6" />
      <circle cx="60" cy="220" r="70" fill="#B8E3FF" opacity="0.5" />
      <circle cx="200" cy="120" r="86" fill="none" stroke="#89D1FF" strokeWidth="2" />
      <circle cx="200" cy="120" r="64" fill="none" stroke="#A6E0FF" strokeWidth="2" strokeDasharray="4 10" />
      <circle cx="200" cy="120" r="40" fill="url(#pa-core-hub)" />
      <circle cx="200" cy="120" r="20" fill="#B8E3FF" />
      <circle cx="200" cy="120" r="8" fill="#FFFFFF" />
      <g fill="#0070F2">
        <circle cx="200" cy="34" r="9" />
        <circle cx="286" cy="120" r="9" />
        <circle cx="200" cy="206" r="9" />
      </g>
      <g fill="#FFFFFF" opacity="0.85">
        <circle cx="114" cy="120" r="6" />
        <rect x="80" y="52" width="12" height="12" rx="3" transform="rotate(20 86 58)" />
        <rect x="300" y="180" width="12" height="12" rx="3" transform="rotate(-14 306 186)" />
      </g>
    </Svg>
  );
}

function EduCoreArtwork() {
  return (
    <Svg>
      <defs>
        <linearGradient id="pa-educore-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#DAFDF5" />
          <stop offset="1" stopColor="#C2FCEE" />
        </linearGradient>
        <linearGradient id="pa-educore-cap" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#07838F" />
          <stop offset="1" stopColor="#035663" />
        </linearGradient>
      </defs>
      <rect width="400" height="240" fill="url(#pa-educore-bg)" />
      <circle cx="340" cy="200" r="80" fill="#64EDD2" opacity="0.35" />
      <circle cx="70" cy="40" r="60" fill="#FFFFFF" opacity="0.6" />
      <path d="M200 44 L268 80 L200 116 L132 80 Z" fill="url(#pa-educore-cap)" />
      <rect x="148" y="112" width="104" height="12" rx="6" fill="#046C7A" />
      <path d="M250 124 L276 152" stroke="#046C7A" strokeWidth="4" strokeLinecap="round" />
      <circle cx="280" cy="158" r="7" fill="#2CE0BF" stroke="#046C7A" strokeWidth="2" />
      <g fill="#046C7A" opacity="0.9">
        <rect x="96" y="168" width="52" height="26" rx="8" />
        <rect x="160" y="152" width="52" height="26" rx="8" fill="#07838F" />
        <rect x="224" y="136" width="52" height="26" rx="8" fill="#2CE0BF" />
      </g>
      <g fill="#FFFFFF" opacity="0.9">
        <circle cx="174" cy="165" r="4" />
        <circle cx="238" cy="149" r="4" />
        <circle cx="122" cy="181" r="4" />
      </g>
      <rect x="300" y="48" width="12" height="12" rx="6" fill="#07838F" opacity="0.7" />
      <rect x="52" y="112" width="10" height="10" rx="5" fill="#046C7A" opacity="0.5" />
    </Svg>
  );
}

function LearnHubArtwork() {
  return (
    <Svg>
      <defs>
        <linearGradient id="pa-learnhub-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#F1ECFF" />
          <stop offset="1" stopColor="#E2D8FF" />
        </linearGradient>
        <linearGradient id="pa-learnhub-book" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#5D36FF" />
          <stop offset="1" stopColor="#2C13AD" />
        </linearGradient>
      </defs>
      <rect width="400" height="240" fill="url(#pa-learnhub-bg)" />
      <circle cx="66" cy="196" r="70" fill="#D3B6FF" opacity="0.5" />
      <g stroke="#B894FF" strokeWidth="2">
        <path d="M200 42 L262 78" />
        <path d="M200 42 L138 78" />
        <path d="M138 78 L262 78" />
      </g>
      <circle cx="200" cy="42" r="14" fill="#5D36FF" stroke="#FFFFFF" strokeWidth="4" />
      <circle cx="262" cy="78" r="10" fill="#470CED" stroke="#FFFFFF" strokeWidth="4" />
      <circle cx="138" cy="78" r="10" fill="#2C13AD" stroke="#FFFFFF" strokeWidth="4" />
      <g transform="translate(58 128)">
        <path d="M64 10 C64 -4 84 -12 104 -12 C124 -12 144 -4 144 10 L144 72 C124 60 108 52 104 52 C100 52 84 60 64 72 Z" fill="url(#pa-learnhub-book)" />
        <path d="M144 10 C144 -4 164 -12 184 -12 C204 -12 224 -4 224 10 L224 72 C204 60 188 52 184 52 C180 52 164 60 144 72 Z" fill="#470CED" opacity="0.85" />
        <rect x="102" y="16" width="128" height="6" rx="3" fill="#FFFFFF" opacity="0.7" />
      </g>
      <g fill="#5D36FF" opacity="0.8">
        <circle cx="306" cy="188" r="6" />
        <circle cx="330" cy="158" r="4" />
      </g>
      <g fill="#FFFFFF" opacity="0.9">
        <rect x="64" y="152" width="10" height="10" rx="5" />
        <circle cx="348" cy="60" r="5" />
      </g>
    </Svg>
  );
}

function InstitutionFlowArtwork() {
  return (
    <Svg>
      <defs>
        <linearGradient id="pa-flow-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FFF8D6" />
          <stop offset="1" stopColor="#FFF3B8" />
        </linearGradient>
        <linearGradient id="pa-flow-step" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FFC933" />
          <stop offset="1" stopColor="#E76500" />
        </linearGradient>
      </defs>
      <rect width="400" height="240" fill="url(#pa-flow-bg)" />
      <circle cx="80" cy="48" r="56" fill="#FFDF72" opacity="0.6" />
      <circle cx="340" cy="200" r="72" fill="#FFFFFF" opacity="0.55" />
      <g>
        <rect x="64" y="116" width="76" height="44" rx="12" fill="#FFFFFF" stroke="#E76500" strokeWidth="3" />
        <circle cx="102" cy="138" r="10" fill="none" stroke="#E76500" strokeWidth="3" />
        <circle cx="102" cy="138" r="4" fill="#E76500" />
        <path d="M164 138 L196 138" stroke="#C35500" strokeWidth="4" strokeLinecap="round" />
        <path d="M186 124 L198 138 L186 152" fill="none" stroke="#C35500" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      <g>
        <rect x="212" y="116" width="76" height="44" rx="12" fill="url(#pa-flow-step)" />
        <circle cx="250" cy="138" r="10" fill="none" stroke="#FFFFFF" strokeWidth="3" />
        <circle cx="250" cy="138" r="4" fill="#FFFFFF" />
        <path d="M312 138 L344 138" stroke="#C35500" strokeWidth="4" strokeLinecap="round" />
        <path d="M334 124 L346 138 L334 152" fill="none" stroke="#C35500" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
      </g>
      <g>
        <rect x="78" y="176" width="76" height="14" rx="7" fill="#FFDF72" />
        <rect x="170" y="176" width="76" height="14" rx="7" fill="#FFC933" />
        <rect x="262" y="176" width="76" height="14" rx="7" fill="#E76500" />
      </g>
      <g fill="#C35500" opacity="0.8">
        <rect x="56" y="58" width="12" height="12" rx="6" />
        <circle cx="320" cy="76" r="6" />
      </g>
    </Svg>
  );
}

function MarketArtwork() {
  return (
    <Svg>
      <defs>
        <linearGradient id="pa-market-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#F5FAE5" />
          <stop offset="1" stopColor="#EBF5CB" />
        </linearGradient>
        <linearGradient id="pa-market-bag" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#36A41D" />
          <stop offset="1" stopColor="#188918" />
        </linearGradient>
      </defs>
      <rect width="400" height="240" fill="url(#pa-market-bg)" />
      <circle cx="330" cy="56" r="76" fill="#BDE986" opacity="0.6" />
      <circle cx="76" cy="192" r="64" fill="#FFFFFF" opacity="0.6" />
      <path d="M176 84 C176 56 200 48 218 48 C238 48 260 58 260 86" fill="none" stroke="#256F3A" strokeWidth="7" strokeLinecap="round" />
      <rect x="152" y="88" width="132" height="104" rx="16" fill="url(#pa-market-bag)" />
      <rect x="150" y="88" width="136" height="16" rx="8" fill="#256F3A" />
      <path d="M178 132 h44 M178 156 h36" stroke="#FFFFFF" strokeWidth="5" strokeLinecap="round" opacity="0.85" />
      <g>
        <circle cx="308" cy="172" r="26" fill="#BDE986" stroke="#97DD40" strokeWidth="4" />
        <circle cx="308" cy="172" r="13" fill="none" stroke="#188918" strokeWidth="3" />
      </g>
      <g>
        <circle cx="96" cy="132" r="20" fill="#97DD40" stroke="#5DC122" strokeWidth="3" />
        <circle cx="96" cy="132" r="9" fill="none" stroke="#256F3A" strokeWidth="2.5" />
      </g>
      <g fill="#188918" opacity="0.8">
        <rect x="280" y="48" width="12" height="12" rx="6" />
        <circle cx="48" cy="84" r="5" />
      </g>
      <g fill="#FFFFFF" opacity="0.9">
        <rect x="196" y="118" width="8" height="8" rx="4" />
        <circle cx="330" cy="132" r="5" />
      </g>
    </Svg>
  );
}

function AssistArtwork() {
  return (
    <Svg>
      <defs>
        <linearGradient id="pa-assist-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FFF0F5" />
          <stop offset="1" stopColor="#FFDCE8" />
        </linearGradient>
        <linearGradient id="pa-assist-bubble" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#FA4F96" />
          <stop offset="1" stopColor="#DF1278" />
        </linearGradient>
      </defs>
      <rect width="400" height="240" fill="url(#pa-assist-bg)" />
      <circle cx="96" cy="52" r="68" fill="#FECBDA" opacity="0.6" />
      <circle cx="330" cy="196" r="80" fill="#FFFFFF" opacity="0.55" />
      <rect x="138" y="72" width="124" height="86" rx="26" fill="url(#pa-assist-bubble)" />
      <path d="M150 156 L134 190 L174 160 Z" fill="url(#pa-assist-bubble)" />
      <path d="M200 92 L209 111 L228 120 L209 129 L200 148 L191 129 L172 120 L191 111 Z" fill="#FFFFFF" />
      <path d="M246 102 L250 114 L262 118 L250 122 L246 134 L242 122 L230 118 L242 114 Z" fill="#FFAFED" />
      <g fill="#DF1278">
        <path d="M108 132 L113 142 L123 147 L113 152 L108 162 L103 152 L93 147 L103 142 Z" />
        <path d="M302 92 L305 99 L312 102 L305 105 L302 112 L299 105 L292 102 L299 99 Z" />
      </g>
      <g fill="#FFFFFF" opacity="0.9">
        <rect x="150" y="196" width="14" height="14" rx="7" />
        <circle cx="272" cy="196" r="6" />
        <circle cx="60" cy="140" r="5" />
      </g>
      <circle cx="300" cy="150" r="4" fill="#BA066C" opacity="0.6" />
    </Svg>
  );
}

function PortalArtwork() {
  return (
    <Svg>
      <defs>
        <linearGradient id="pa-portal-bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#EFF8FF" />
          <stop offset="1" stopColor="#D1EFFF" />
        </linearGradient>
        <linearGradient id="pa-portal-door" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0070F2" />
          <stop offset="1" stopColor="#0040A3" />
        </linearGradient>
      </defs>
      <rect width="400" height="240" fill="url(#pa-portal-bg)" />
      <circle cx="340" cy="48" r="84" fill="#B8E3FF" opacity="0.55" />
      <circle cx="70" cy="208" r="72" fill="#FFFFFF" opacity="0.7" />
      <circle cx="200" cy="104" r="64" fill="none" stroke="#89D1FF" strokeWidth="2" strokeDasharray="5 9" />
      <circle cx="200" cy="104" r="48" fill="none" stroke="#A6E0FF" strokeWidth="2" />
      <circle cx="200" cy="104" r="30" fill="url(#pa-portal-door)" />
      <circle cx="200" cy="94" r="11" fill="#FFFFFF" />
      <path d="M188 134 A12 12 0 0 1 212 134 L212 146 L188 146 Z" fill="#FFFFFF" />
      <g fill="#0070F2" opacity="0.9">
        <circle cx="136" cy="104" r="6" />
        <circle cx="264" cy="104" r="6" />
        <circle cx="200" cy="40" r="6" />
        <circle cx="200" cy="168" r="6" />
      </g>
      <g fill="#FFFFFF" opacity="0.85">
        <rect x="72" y="52" width="12" height="12" rx="3" transform="rotate(18 78 58)" />
        <rect x="308" y="168" width="12" height="12" rx="3" transform="rotate(-12 314 174)" />
      </g>
    </Svg>
  );
}

const artworkByCode: Record<string, () => JSX.Element> = {
  CAMPUSONE_CORE: CampusOneArtwork,
  EDUCORE: EduCoreArtwork,
  LEARNHUB: LearnHubArtwork,
  INSTITUTIONFLOW: InstitutionFlowArtwork,
  CAMPUSONE_MARKET: MarketArtwork,
  CAMPUSONE_ASSIST: AssistArtwork,
};